import { StatsigClient } from "@statsig/js-client";
import { Navigate, Routes, Route } from "react-router-dom";

import ROUTES from "@app/common/routes";
import type { IntegrationScope } from "@app/common/types";
import Config from "@app/config";

import CalendarIntegrationConfig from "./CalendarIntegrationConfig";
import HubSpotIntegrationConfig from "./HubSpotIntegrationConfig";
import ProductivityIntegrationConfig from "./ProductivityIntegrationConfig";
import SalesforceIntegrationConfig from "./SalesforceIntegrationConfig";
import UserSlackIntegrationConfig from "./UserSlackIntegrationConfig";
import UserTeamsIntegrationConfig from "./UserTeamsIntegrationConfig";
import WebhooksIntegrationConfig from "./WebhooksIntegrationConfig";
import WorkspaceSlackIntegrationConfig from "./WorkspaceSlackIntegrationConfig";
import ZapierIntegrationConfig from "./ZapierIntegrationConfig";

const WORKSPACE_INTEGRATION_ROUTES = [
  { path: "slack", element: <WorkspaceSlackIntegrationConfig /> },
  { path: "teams", element: null },
];

const USER_INTEGRATION_ROUTES = [
  {
    path: "calendar",
    routes: [
      {
        path: "google",
        element: <CalendarIntegrationConfig providerId="google_calendar" />,
      },
      {
        path: "outlook",
        element: (
          <CalendarIntegrationConfig providerId="microsoft_graph_calendar" />
        ),
      },
      {
        path: "zoom",
        element: <CalendarIntegrationConfig providerId="zoom_calendar" />,
      },
    ],
  },
  {
    path: "productivity",
    routes: [
      {
        path: "google",
        element: <ProductivityIntegrationConfig providerId="google_email" />,
      },
      {
        path: "outlook",
        element: (
          <ProductivityIntegrationConfig providerId="microsoft_graph_email" />
        ),
      },
      {
        path: "slack",
        element: <ProductivityIntegrationConfig providerId="slack_ingest" />,
      },
      {
        path: "teams",
        element: (
          <ProductivityIntegrationConfig providerId="microsoft_graph_teams_chat_ingest" />
        ),
      },
    ],
  },
  {
    path: "messaging",
    routes: [
      { path: "slack", element: <UserSlackIntegrationConfig /> },
      { path: "teams", element: <UserTeamsIntegrationConfig /> },
    ],
  },
  {
    path: "workflow",
    routes: [
      { path: "webhooks", element: <WebhooksIntegrationConfig /> },
      { path: "zapier", element: <ZapierIntegrationConfig /> },
    ],
  },
  {
    path: "crm",
    routes: [
      { path: "hubspot", element: <HubSpotIntegrationConfig /> },
      {
        path: "salesforce",
        disabled: () =>
          !(
            Config.flags.enableSalesforceIntegration ||
            StatsigClient.instance().checkGate("enable_salesforce")
          ),
        element: <SalesforceIntegrationConfig />,
      },
    ],
  },
];

const IntegrationDetail = ({ scope }: { scope: IntegrationScope }) => {
  if (scope === "workspace") {
    return (
      <Routes>
        {WORKSPACE_INTEGRATION_ROUTES.map(({ path, element }) => {
          if (!element) return null;
          return <Route key={path} path={path} element={element} />;
        })}
        <Route
          path="*"
          element={
            <Navigate to={ROUTES.INTEGRATIONS_WORKSPACE_SLACK} replace />
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      {USER_INTEGRATION_ROUTES.map((section) => (
        <Route key={section.path} path={section.path}>
          {section.routes.map(({ path, element, disabled }) => {
            if (!element || disabled?.()) return null;
            return <Route key={path} path={path} element={element} />;
          })}
        </Route>
      ))}
      <Route
        path="*"
        element={<Navigate to={ROUTES.INTEGRATIONS_DEFAULT_SECTION} replace />}
      />
    </Routes>
  );
};

export default IntegrationDetail;
